import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, Button, TextField, Box, Alert, CircularProgress, Paper, Divider, Chip, IconButton, Tabs, Tab, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { config } from '../config/env';

interface CvShareData {
  optimizedShortProfile?: string;
  optimizedWorkExperiences?: any[];
  optimizedCvText?: string;
  relevantItSkills?: any[];
  relevantLanguages?: any[];
  relevantCertifications?: any[];
  relevantCoreCompetencies?: any[];
  relevantAdditionalInfo?: string;
  relevantHobbies?: any[];
  relevantIndustries?: any[];
  relevantEducations?: any[];
  itSkills?: any[];
  languages?: any[];
  certifications?: any[];
  coreCompetencies?: any[];
  educations?: any[];
  workExperience?: any[];
  applicationId?: string;
  expiresAt?: string;
  status?: string;
  // Persönliche Informationen
  fullName?: string;
  birthYear?: string;
  nationality?: string;
  residencePermit?: string;
  maritalStatus?: string;
  profileImage?: string;
}

// Mapping-Funktion für Sprachlevel-Text zu CEFR Select-Werten
const mapLanguageLevelToSelectValue = (levelText: string): string => {
  if (!levelText) return '';
  const normalizedText = levelText.toLowerCase().trim();
  
  // Direkte CEFR-Codes
  const directMatch: { [key: string]: string } = {
    'a1': 'A1', 'a2': 'A2', 'b1': 'B1', 'b2': 'B2', 'c1': 'C1', 'c2': 'C2', 'native': 'native'
  };
  if (directMatch[normalizedText]) return directMatch[normalizedText];
  
  const levelMap: { [key: string]: string } = {
    'muttersprache': 'native', 'langue maternelle': 'native', 'madrelingua': 'native',
    'native speaker': 'native', 'muttersprachlich': 'native', 'erstsprache': 'native',
    'annähernd muttersprachlich': 'C2', 'near-native': 'C2', 'near native': 'C2', 'proficient': 'C2',
    'nahezu muttersprachlich': 'C2', 'quasi langue maternelle': 'C2', 'quasi madrelingua': 'C2',
    'fachkundig': 'C1', 'verhandlungssicher': 'C1', 'fliessend': 'C1', 'fließend': 'C1',
    'fluent': 'C1', 'advanced': 'C1', 'courant': 'C1', 'sehr gut': 'C1',
    'gute kenntnisse': 'B2', 'gut': 'B2', 'good': 'B2', 'fortgeschritten': 'B2',
    'upper intermediate': 'B2', 'bien': 'B2', 'buono': 'B2',
    'intermediate': 'B1', 'mittelstufe': 'B1', 'mittel': 'B1', 'moyen': 'B1', 'intermedio': 'B1',
    'grundlegende kenntnisse': 'A2', 'elementary': 'A2', 'grundkenntnisse': 'A2', 'basiskenntnisse': 'A2', 'basis': 'A2',
    'anfänger': 'A1', 'beginner': 'A1', 'débutant': 'A1', 'principiante': 'A1', 'wenig': 'A1', 'gering': 'A1'
  };
  
  if (levelMap[normalizedText]) return levelMap[normalizedText];
  
  for (const [key, value] of Object.entries(levelMap)) {
    if (normalizedText.includes(key) || key.includes(normalizedText)) return value;
  }
  
  const ceferMatch = normalizedText.match(/^(a1|a2|b1|b2|c1|c2)\b/);
  if (ceferMatch) return ceferMatch[1].toUpperCase();
  
  return '';
};

// Mapping-Funktion für IT-Skill-Level-Text zu Select-Werten
const mapItSkillLevelToSelectValue = (levelText: string): string => {
  if (!levelText) return '';
  const normalizedText = levelText.toLowerCase().trim();
  
  // Direkte Werte
  const directMatch: { [key: string]: string } = {
    'beginner': 'beginner', 'intermediate': 'intermediate', 'professional': 'professional', 'expert': 'expert'
  };
  if (directMatch[normalizedText]) return directMatch[normalizedText];
  
  const levelMap: { [key: string]: string } = {
    // Anfänger / Beginner
    'anfänger': 'beginner', 'débutant': 'beginner', 'principiante': 'beginner',
    'grundkenntnisse': 'beginner', 'grundlegend': 'beginner', 'basic': 'beginner', 'wenig': 'beginner',
    'basiskenntnisse': 'beginner', 'gering': 'beginner',
    // Fortgeschritten / Intermediate
    'fortgeschritten': 'intermediate', 'avancé': 'intermediate', 'avanzato': 'intermediate',
    'gute kenntnisse': 'intermediate', 'gut': 'intermediate', 'good': 'intermediate',
    'mittel': 'intermediate', 'moyen': 'intermediate', 'intermedio': 'intermediate',
    // Profi / Professional
    'profi': 'professional', 'professionnel': 'professional', 'professionista': 'professional',
    'sehr gute kenntnisse': 'professional', 'sehr gut': 'professional', 'very good': 'professional',
    'vertieft': 'professional', 'umfassend': 'professional',
    // Experte / Expert
    'experte': 'expert', 'expert': 'expert', 'esperto': 'expert',
    'hervorragend': 'expert', 'exzellent': 'expert', 'excellent': 'expert',
    'meister': 'expert', 'master': 'expert'
  };
  
  if (levelMap[normalizedText]) return levelMap[normalizedText];
  
  for (const [key, value] of Object.entries(levelMap)) {
    if (normalizedText.includes(key) || key.includes(normalizedText)) return value;
  }
  
  return '';
};

const normalizeProfileImageValue = (value: any): string => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '';
  }
  if (value && typeof value === 'object') {
    const base64Data = typeof value.data === 'string' ? value.data.trim() : '';
    const mimeType = typeof value.type === 'string' ? value.type.trim() : '';
    if (base64Data) {
      return mimeType ? `data:${mimeType};base64,${base64Data}` : `data:image/jpeg;base64,${base64Data}`;
    }
  }
  return '';
};

const CvSharePage: React.FC<{ token: string }> = ({ token }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cvData, setCvData] = useState<CvShareData | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const isReadOnly = isSaved || isExpired;
  const contentRef = useRef<HTMLDivElement>(null);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    // Scroll zum Anfang des Content-Bereichs
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };
  
  // Formular-States
  const [optimizedShortProfile, setOptimizedShortProfile] = useState('');
  const [relevantItSkills, setRelevantItSkills] = useState<any[]>([]);
  const [newItSkillName, setNewItSkillName] = useState<string>('');
  const [newItSkillLevel, setNewItSkillLevel] = useState<string>('');
  const [editingItSkillIndex, setEditingItSkillIndex] = useState<number | null>(null);
  const [relevantLanguages, setRelevantLanguages] = useState<any[]>([]);
  const [newLanguageName, setNewLanguageName] = useState<string>('');
  const [newLanguageLevel, setNewLanguageLevel] = useState<string>('');
  const [editingLanguageIndex, setEditingLanguageIndex] = useState<number | null>(null);
  const [relevantCertifications, setRelevantCertifications] = useState<any[]>([]);
  const [newCertificationName, setNewCertificationName] = useState<string>('');
  const [newCertificationIssuer, setNewCertificationIssuer] = useState<string>('');
  const [newCertificationDate, setNewCertificationDate] = useState<string>('');
  const [editingCertificationIndex, setEditingCertificationIndex] = useState<number | null>(null);
  const [relevantCoreCompetencies, setRelevantCoreCompetencies] = useState<any[]>([]);
  const [newCoreCompetency, setNewCoreCompetency] = useState<string>('');
  const [editingCoreCompetencyIndex, setEditingCoreCompetencyIndex] = useState<number | null>(null);
  const [relevantAdditionalInfo, setRelevantAdditionalInfo] = useState<Array<{ info: string; details: string }>>([]);
  const [newAdditionalInfoInfo, setNewAdditionalInfoInfo] = useState<string>('');
  const [newAdditionalInfoDetails, setNewAdditionalInfoDetails] = useState<string>('');
  const [editingAdditionalInfoIndex, setEditingAdditionalInfoIndex] = useState<number | null>(null);
  const [relevantHobbies, setRelevantHobbies] = useState<any[]>([]);
  const [newHobbyName, setNewHobbyName] = useState<string>('');
  const [newHobbyDescription, setNewHobbyDescription] = useState<string>('');
  const [editingHobbyIndex, setEditingHobbyIndex] = useState<number | null>(null);
  const [optimizedWorkExperiences, setOptimizedWorkExperiences] = useState<any[]>([]);
  const [relevantEducations, setRelevantEducations] = useState<any[]>([]);
  const [newEducationDegree, setNewEducationDegree] = useState<string>('');
  const [newEducationInstitution, setNewEducationInstitution] = useState<string>('');
  const [newEducationStartDate, setNewEducationStartDate] = useState<string>('');
  const [newEducationEndDate, setNewEducationEndDate] = useState<string>('');
  const [editingEducationIndex, setEditingEducationIndex] = useState<number | null>(null);
  const [relevantIndustries, setRelevantIndustries] = useState<any[]>([]);
  const [newIndustryName, setNewIndustryName] = useState<string>('');
  const [newIndustryYears, setNewIndustryYears] = useState<string>('');
  const [editingIndustryIndex, setEditingIndustryIndex] = useState<number | null>(null);
  
  // Persönliche Informationen States
  const [fullName, setFullName] = useState<string>('');
  const [birthYear, setBirthYear] = useState<string>('');
  const [nationality, setNationality] = useState<string>('');
  const [residencePermit, setResidencePermit] = useState<string>('');
  const [maritalStatus, setMaritalStatus] = useState<string>('');
  const [profileImage, setProfileImage] = useState<string>('');
  
  useEffect(() => {
    loadCvData();
  }, [token]);
  
  const loadCvData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsExpired(false);
      setIsSaved(false);
      
      console.log('🔗 Lade CV-Daten für Token:', token);
      console.log('🔗 API URL:', `${config.backend.baseUrl}/api/cv-share/${token}`);
      
      const response = await fetch(`${config.backend.baseUrl}/api/cv-share/${token}`);
      
      console.log('📡 Response Status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Fehler:', errorData);
        if (response.status === 410) {
          setIsExpired(true);
          setError('Dieser Link ist abgelaufen.');
        } else {
          setError(errorData.error || 'Fehler beim Laden der CV-Daten');
        }
        return;
      }
      
      const result = await response.json();
      console.log('✅ API Response:', result);
      
      if (result.success && result.data) {
        const data = result.data;
        console.log('📊 CV-Daten (vollständig):', JSON.stringify(data, null, 2));
        console.log('📊 ApplicationId aus Response:', data.applicationId);
        console.log('📊 Optimierte Felder vorhanden:', {
          optimizedShortProfile: !!data.optimizedShortProfile,
          optimizedShortProfileLength: data.optimizedShortProfile?.length || 0,
          optimizedWorkExperiences: !!data.optimizedWorkExperiences,
          optimizedWorkExperiencesCount: data.optimizedWorkExperiences?.length || 0,
          relevantItSkills: !!data.relevantItSkills,
          relevantItSkillsCount: data.relevantItSkills?.length || 0,
          relevantLanguages: !!data.relevantLanguages,
          relevantLanguagesCount: data.relevantLanguages?.length || 0,
          relevantAdditionalInfo: !!data.relevantAdditionalInfo,
          relevantAdditionalInfoLength: data.relevantAdditionalInfo?.length || 0,
          relevantCertifications: !!data.relevantCertifications,
          relevantCoreCompetencies: !!data.relevantCoreCompetencies,
          relevantHobbies: !!data.relevantHobbies,
          relevantEducations: !!data.relevantEducations
        });
        
        setCvData(data);
        
        // Setze Formular-Werte - NUR optimierte/relevante Felder (keine Fallbacks auf nicht-optimierte)
        const shortProfile = data.optimizedShortProfile || '';
        // Normalisiere IT-Skills: Stelle sicher, dass sie als Array von Objekten mit 'skill'/'name' und 'level' Feldern vorliegen
        let itSkills: any[] = [];
        if (Array.isArray(data.relevantItSkills)) {
          itSkills = data.relevantItSkills.map((skill: any) => {
            if (typeof skill === 'string') {
              return { skill: skill, name: skill, level: '', proficiency: '' };
            } else if (skill && typeof skill === 'object') {
              const rawLevel = skill.level || skill.proficiency || '';
              const mappedLevel = mapItSkillLevelToSelectValue(rawLevel);
              return {
                skill: skill.skill || skill.name || '',
                name: skill.name || skill.skill || '',
                level: mappedLevel,
                proficiency: mappedLevel
              };
            }
            return { skill: '', name: '', level: '', proficiency: '' };
          }).filter((skill: any) => skill.skill || skill.name);
        }
        // Normalisiere Sprachen: Stelle sicher, dass sie als Array von Objekten mit 'language'/'name' und 'level' Feldern vorliegen
        let languages: any[] = [];
        if (Array.isArray(data.relevantLanguages)) {
          languages = data.relevantLanguages.map((lang: any) => {
            if (typeof lang === 'string') {
              return { language: lang, name: lang, level: '', proficiency: '' };
            } else if (lang && typeof lang === 'object') {
              const rawLevel = lang.level || lang.proficiency || '';
              const mappedLevel = mapLanguageLevelToSelectValue(rawLevel);
              return {
                language: lang.language || lang.name || '',
                name: lang.name || lang.language || '',
                level: mappedLevel,
                proficiency: mappedLevel
              };
            }
            return { language: '', name: '', level: '', proficiency: '' };
          }).filter((lang: any) => lang.language || lang.name);
        }
        // Normalisiere Zertifikate: Stelle sicher, dass sie als Array von Objekten mit 'name'/'certificate', 'issuer' und 'date' Feldern vorliegen
        let certifications: any[] = [];
        if (Array.isArray(data.relevantCertifications)) {
          certifications = data.relevantCertifications.map((cert: any) => {
            if (typeof cert === 'string') {
              return { name: cert, certificate: cert, issuer: '', date: '' };
            } else if (cert && typeof cert === 'object') {
              return {
                name: cert.name || cert.certificate || '',
                certificate: cert.certificate || cert.name || '',
                description: cert.description || '',
                issuer: cert.issuer || '',
                date: cert.date || ''
              };
            }
            return { name: '', certificate: '', description: '', issuer: '', date: '' };
          }).filter((cert: any) => cert.name || cert.certificate);
        }
        // Normalisiere Kernkompetenzen: Stelle sicher, dass sie als Array von Objekten mit 'competency' Feld vorliegen
        let coreCompetencies: any[] = [];
        if (Array.isArray(data.relevantCoreCompetencies)) {
          coreCompetencies = data.relevantCoreCompetencies.map((comp: any) => {
            if (typeof comp === 'string') {
              return { competency: comp };
            } else if (comp && typeof comp === 'object') {
              return { competency: comp.competency || comp.name || '' };
            }
            return { competency: '' };
          }).filter((comp: any) => comp.competency);
        }
        // Normalisiere zusätzliche Informationen: Stelle sicher, dass sie als Array von Objekten mit 'info' und 'details' Feldern vorliegen
        let additionalInfo: Array<{ info: string; details: string }> = [];
        if (Array.isArray(data.relevantAdditionalInfo)) {
          additionalInfo = data.relevantAdditionalInfo.map((item: any) => {
            if (typeof item === 'string') {
              // Wenn es ein String ist, versuche es zu parsen (Format: "info: details")
              if (item.includes(':')) {
                const [info, ...detailsParts] = item.split(':');
                return { info: info.trim(), details: detailsParts.join(':').trim() };
              }
              return { info: item.trim(), details: '' };
            } else if (item && typeof item === 'object') {
              return {
                info: item.info || '',
                details: item.details || ''
              };
            }
            return { info: '', details: '' };
          }).filter((item: any) => item.info);
        } else if (typeof data.relevantAdditionalInfo === 'string' && data.relevantAdditionalInfo.trim()) {
          // Wenn es ein String ist, versuche es zu parsen
          const lines = data.relevantAdditionalInfo.split('\n').filter((line: string) => line.trim());
          additionalInfo = lines.map((line: string) => {
            if (line.includes(':')) {
              const [info, ...detailsParts] = line.split(':');
              return { info: info.trim(), details: detailsParts.join(':').trim() };
            }
            return { info: line.trim(), details: '' };
          });
        }
        // Normalisiere Hobbys: Stelle sicher, dass sie als Array von Objekten mit 'hobby'/'name' und 'description' Feldern vorliegen
        let hobbies: any[] = [];
        if (Array.isArray(data.relevantHobbies)) {
          hobbies = data.relevantHobbies.map((hobby: any) => {
            if (typeof hobby === 'string') {
              return { hobby: hobby, name: hobby, description: '' };
            } else if (hobby && typeof hobby === 'object') {
              return {
                hobby: hobby.hobby || hobby.name || '',
                name: hobby.name || hobby.hobby || '',
                description: hobby.description || ''
              };
            }
            return { hobby: '', name: '', description: '' };
          }).filter((hobby: any) => hobby.hobby || hobby.name);
        }
        const workExperiences = Array.isArray(data.optimizedWorkExperiences) ? data.optimizedWorkExperiences : [];
        // Normalisiere Ausbildungen: Stelle sicher, dass sie als Array von Objekten mit 'degree'/'title', 'institution'/'school', 'startDate' und 'endDate' Feldern vorliegen
        let educations: any[] = [];
        if (Array.isArray(data.relevantEducations)) {
          educations = data.relevantEducations.map((edu: any) => {
            if (typeof edu === 'string') {
              return { degree: edu, title: edu, institution: '', school: '', description: '', startDate: '', endDate: '' };
            } else if (edu && typeof edu === 'object') {
              return {
                degree: edu.degree || edu.title || '',
                title: edu.title || edu.degree || '',
                institution: edu.institution || edu.school || '',
                school: edu.school || edu.institution || '',
                description: edu.description || '',
                startDate: edu.startDate || '',
                endDate: edu.endDate || ''
              };
            }
            return { degree: '', title: '', institution: '', school: '', description: '', startDate: '', endDate: '' };
          }).filter((edu: any) => edu.degree || edu.title);
        }
        // Normalisiere Branchen: Stelle sicher, dass sie als Array von Objekten mit 'industry'/'name' und 'years' Feldern vorliegen
        let industries: any[] = [];
        if (Array.isArray(data.relevantIndustries)) {
          industries = data.relevantIndustries.map((industry: any) => {
            if (typeof industry === 'string') {
              return { industry: industry, name: industry, years: '' };
            } else if (industry && typeof industry === 'object') {
              return {
                industry: industry.industry || industry.name || '',
                name: industry.name || industry.industry || '',
                years: industry.years || ''
              };
            }
            return { industry: '', name: '', years: '' };
          }).filter((industry: any) => industry.industry || industry.name);
        }
        
        console.log('📝 Setze Formular-Werte:', {
          applicationId: data.applicationId,
          shortProfile: shortProfile.substring(0, 50) + (shortProfile.length > 50 ? '...' : ''),
          shortProfileLength: shortProfile.length,
          shortProfileRaw: data.optimizedShortProfile,
          itSkillsCount: itSkills.length,
          languagesCount: languages.length,
          workExperiencesCount: workExperiences.length,
          additionalInfoLength: additionalInfo.length,
          coreCompetenciesCount: coreCompetencies.length,
          industriesCount: industries.length
        });
        
        setOptimizedShortProfile(shortProfile);
        setRelevantItSkills(itSkills);
        setRelevantLanguages(languages);
        setRelevantCertifications(certifications);
        setRelevantCoreCompetencies(coreCompetencies);
        setRelevantAdditionalInfo(additionalInfo);
        setRelevantHobbies(hobbies);
        setOptimizedWorkExperiences(workExperiences);
        setRelevantEducations(educations);
        setRelevantIndustries(industries);
        
        // Persönliche Informationen setzen (verschiedene mögliche Feldnamen berücksichtigen)
        setFullName(data.fullName || data.name || '');
        setBirthYear(data.birthYear || data.yearOfBirth || data.geburtsjahr || '');
        setNationality(data.nationality || data.nationalität || '');
        setResidencePermit(data.residencePermit || data.aufenthaltsbewilligung || data.permit || '');
        setMaritalStatus(data.maritalStatus || data.civilStatus || data.zivilstand || '');
        setProfileImage(normalizeProfileImageValue(data.profileImage || data.photo || data.bild || ''));
        setIsSaved(data.status === 'approved');
        
        console.log('📊 Persönliche Informationen:', {
          fullName: data.fullName || data.name,
          birthYear: data.birthYear || data.yearOfBirth,
          nationality: data.nationality,
          residencePermit: data.residencePermit,
          maritalStatus: data.maritalStatus || data.civilStatus,
          profileImage: !!data.profileImage
        });
        
        if (data.expiresAt) {
          setExpiresAt(data.expiresAt);
          const expires = new Date(data.expiresAt);
          if (new Date() > expires) {
            setIsExpired(true);
          }
        }
      } else {
        console.warn('⚠️ Keine CV-Daten in Response:', result);
        setError('Keine CV-Daten gefunden');
      }
    } catch (err: any) {
      console.error('❌ Fehler beim Laden der CV-Daten:', err);
      setError('Fehler beim Laden der CV-Daten: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handler zum Entfernen einer Kernkompetenz
  const handleDeleteCoreCompetency = (index: number) => {
    setRelevantCoreCompetencies(prev => prev.filter((_, i) => i !== index));
    setEditingCoreCompetencyIndex(null);
  };
  
  // Handler zum Bearbeiten einer Kernkompetenz
  const handleEditCoreCompetency = (index: number, value: string) => {
    setRelevantCoreCompetencies(prev => prev.map((comp, i) => 
      i === index ? { competency: value } : comp
    ));
  };
  
  // Handler zum Hinzufügen einer neuen Kernkompetenz
  const handleAddCoreCompetency = () => {
    if (newCoreCompetency.trim()) {
      setRelevantCoreCompetencies(prev => [...prev, { competency: newCoreCompetency.trim() }]);
      setNewCoreCompetency('');
    }
  };
  
  // Handler zum Entfernen eines IT-Skills
  const handleDeleteItSkill = (index: number) => {
    setRelevantItSkills(prev => prev.filter((_, i) => i !== index));
    setEditingItSkillIndex(null);
  };
  
  // Handler zum Bearbeiten eines IT-Skills
  const handleEditItSkill = (index: number, field: 'skill' | 'name' | 'level' | 'proficiency', value: string) => {
    setRelevantItSkills(prev => prev.map((skill, i) => {
      if (i === index) {
        const updated = { ...skill };
        if (field === 'skill' || field === 'name') {
          updated.skill = value;
          updated.name = value;
        } else if (field === 'level' || field === 'proficiency') {
          updated.level = value;
          updated.proficiency = value;
        }
        return updated;
      }
      return skill;
    }));
  };
  
  // Handler zum Hinzufügen eines neuen IT-Skills
  const handleAddItSkill = () => {
    if (newItSkillName.trim()) {
      setRelevantItSkills(prev => [...prev, { 
        skill: newItSkillName.trim(), 
        name: newItSkillName.trim(),
        level: newItSkillLevel.trim() || '',
        proficiency: newItSkillLevel.trim() || ''
      }]);
      setNewItSkillName('');
      setNewItSkillLevel('');
    }
  };
  
  // Handler zum Entfernen einer Sprache
  const handleDeleteLanguage = (index: number) => {
    setRelevantLanguages(prev => prev.filter((_, i) => i !== index));
    setEditingLanguageIndex(null);
  };
  
  // Handler zum Bearbeiten einer Sprache
  const handleEditLanguage = (index: number, field: 'language' | 'name' | 'level' | 'proficiency', value: string) => {
    setRelevantLanguages(prev => prev.map((lang, i) => {
      if (i === index) {
        const updated = { ...lang };
        if (field === 'language' || field === 'name') {
          updated.language = value;
          updated.name = value;
        } else if (field === 'level' || field === 'proficiency') {
          updated.level = value;
          updated.proficiency = value;
        }
        return updated;
      }
      return lang;
    }));
  };
  
  // Handler zum Hinzufügen einer neuen Sprache
  const handleAddLanguage = () => {
    if (newLanguageName.trim()) {
      setRelevantLanguages(prev => [...prev, { 
        language: newLanguageName.trim(), 
        name: newLanguageName.trim(),
        level: newLanguageLevel.trim() || '',
        proficiency: newLanguageLevel.trim() || ''
      }]);
      setNewLanguageName('');
      setNewLanguageLevel('');
    }
  };
  
  // Handler zum Entfernen einer Branche
  const handleDeleteIndustry = (index: number) => {
    setRelevantIndustries(prev => prev.filter((_, i) => i !== index));
    setEditingIndustryIndex(null);
  };
  
  // Handler zum Bearbeiten einer Branche
  const handleEditIndustry = (index: number, field: 'industry' | 'name' | 'years', value: string) => {
    setRelevantIndustries(prev => prev.map((industry, i) => {
      if (i === index) {
        const updated = { ...industry };
        if (field === 'industry' || field === 'name') {
          updated.industry = value;
          updated.name = value;
        } else if (field === 'years') {
          updated.years = value;
        }
        return updated;
      }
      return industry;
    }));
  };
  
  // Handler zum Hinzufügen einer neuen Branche
  const handleAddIndustry = () => {
    if (newIndustryName.trim()) {
      setRelevantIndustries(prev => [...prev, { 
        industry: newIndustryName.trim(), 
        name: newIndustryName.trim(),
        years: newIndustryYears.trim() || ''
      }]);
      setNewIndustryName('');
      setNewIndustryYears('');
    }
  };
  
  // Handler zum Entfernen einer Ausbildung
  const handleDeleteEducation = (index: number) => {
    setRelevantEducations(prev => prev.filter((_, i) => i !== index));
    setEditingEducationIndex(null);
  };
  
  // Handler zum Bearbeiten einer Ausbildung
  const handleEditEducation = (index: number, field: 'degree' | 'title' | 'institution' | 'school' | 'description' | 'startDate' | 'endDate', value: string) => {
    setRelevantEducations(prev => prev.map((edu, i) => {
      if (i === index) {
        const updated = { ...edu };
        if (field === 'degree' || field === 'title') {
          updated.degree = value;
          updated.title = value;
        } else if (field === 'institution' || field === 'school') {
          updated.institution = value;
          updated.school = value;
        } else if (field === 'startDate') {
          updated.startDate = value;
        } else if (field === 'endDate') {
          updated.endDate = value;
        }
        return updated;
      }
      return edu;
    }));
  };
  
  // Handler zum Hinzufügen einer neuen Ausbildung
  const handleAddEducation = () => {
    if (newEducationDegree.trim()) {
      setRelevantEducations(prev => [...prev, { 
        degree: newEducationDegree.trim(),
        title: newEducationDegree.trim(),
        institution: newEducationInstitution.trim() || '',
        school: newEducationInstitution.trim() || '',
        startDate: newEducationStartDate.trim() || '',
        endDate: newEducationEndDate.trim() || ''
      }]);
      setNewEducationDegree('');
      setNewEducationInstitution('');
      setNewEducationStartDate('');
      setNewEducationEndDate('');
    }
  };
  
  // Handler zum Entfernen eines Zertifikats
  const handleDeleteCertification = (index: number) => {
    setRelevantCertifications(prev => prev.filter((_, i) => i !== index));
    setEditingCertificationIndex(null);
  };
  
  // Handler zum Bearbeiten eines Zertifikats
  const handleEditCertification = (index: number, field: 'name' | 'certificate' | 'description' | 'issuer' | 'date', value: string) => {
    setRelevantCertifications(prev => prev.map((cert, i) => {
      if (i === index) {
        const updated = { ...cert };
        if (field === 'name' || field === 'certificate') {
          updated.name = value;
          updated.certificate = value;
        } else if (field === 'description') {
          updated.description = value;
        } else if (field === 'issuer') {
          updated.issuer = value;
        } else if (field === 'date') {
          updated.date = value;
        }
        return updated;
      }
      return cert;
    }));
  };
  
  // Handler zum Hinzufügen eines neuen Zertifikats
  const handleAddCertification = () => {
    if (newCertificationName.trim()) {
      setRelevantCertifications(prev => [...prev, { 
        name: newCertificationName.trim(),
        certificate: newCertificationName.trim(),
        issuer: newCertificationIssuer.trim() || '',
        date: newCertificationDate.trim() || ''
      }]);
      setNewCertificationName('');
      setNewCertificationIssuer('');
      setNewCertificationDate('');
    }
  };
  
  // Handler zum Entfernen eines Hobbys
  const handleDeleteHobby = (index: number) => {
    setRelevantHobbies(prev => prev.filter((_, i) => i !== index));
    setEditingHobbyIndex(null);
  };
  
  // Handler zum Bearbeiten eines Hobbys
  const handleEditHobby = (index: number, field: 'hobby' | 'name' | 'description', value: string) => {
    setRelevantHobbies(prev => prev.map((hobby, i) => {
      if (i === index) {
        const updated = { ...hobby };
        if (field === 'hobby' || field === 'name') {
          updated.hobby = value;
          updated.name = value;
        } else if (field === 'description') {
          updated.description = value;
        }
        return updated;
      }
      return hobby;
    }));
  };
  
  // Handler zum Hinzufügen eines neuen Hobbys
  const handleAddHobby = () => {
    if (newHobbyName.trim()) {
      setRelevantHobbies(prev => [...prev, { 
        hobby: newHobbyName.trim(),
        name: newHobbyName.trim(),
        description: newHobbyDescription.trim() || ''
      }]);
      setNewHobbyName('');
      setNewHobbyDescription('');
    }
  };
  
  // Handler zum Entfernen einer zusätzlichen Information
  const handleDeleteAdditionalInfo = (index: number) => {
    setRelevantAdditionalInfo(prev => prev.filter((_, i) => i !== index));
    setEditingAdditionalInfoIndex(null);
  };
  
  // Handler zum Bearbeiten einer zusätzlichen Information
  const handleEditAdditionalInfo = (index: number, field: 'info' | 'details', value: string) => {
    setRelevantAdditionalInfo(prev => prev.map((info, i) => 
      i === index ? { ...info, [field]: value } : info
    ));
  };
  
  // Handler zum Hinzufügen einer neuen zusätzlichen Information
  const handleAddAdditionalInfo = () => {
    if (newAdditionalInfoInfo.trim()) {
      setRelevantAdditionalInfo(prev => [...prev, { 
        info: newAdditionalInfoInfo.trim(),
        details: newAdditionalInfoDetails.trim() || ''
      }]);
      setNewAdditionalInfoInfo('');
      setNewAdditionalInfoDetails('');
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const updateData: any = {};
      
      if (optimizedShortProfile !== cvData?.optimizedShortProfile) {
        updateData.optimizedShortProfile = optimizedShortProfile;
      }
      // Normalisiere IT-Skills für den Vergleich
      const normalizedCurrentItSkills = relevantItSkills.map((skill: any) => ({
        skill: typeof skill === 'string' ? skill : (skill?.skill || skill?.name || ''),
        name: typeof skill === 'string' ? skill : (skill?.name || skill?.skill || ''),
        level: typeof skill === 'object' ? (skill?.level || skill?.proficiency || '') : '',
        proficiency: typeof skill === 'object' ? (skill?.proficiency || skill?.level || '') : ''
      })).filter((skill: any) => skill.skill || skill.name);
      
      const normalizedOriginalItSkills = (cvData?.relevantItSkills || []).map((skill: any) => ({
        skill: typeof skill === 'string' ? skill : (skill?.skill || skill?.name || ''),
        name: typeof skill === 'string' ? skill : (skill?.name || skill?.skill || ''),
        level: typeof skill === 'object' ? (skill?.level || skill?.proficiency || '') : '',
        proficiency: typeof skill === 'object' ? (skill?.proficiency || skill?.level || '') : ''
      })).filter((skill: any) => skill.skill || skill.name);
      
      if (JSON.stringify(normalizedCurrentItSkills) !== JSON.stringify(normalizedOriginalItSkills)) {
        updateData.relevantItSkills = normalizedCurrentItSkills;
      }
      // Normalisiere Sprachen für den Vergleich
      const normalizedCurrentLanguages = relevantLanguages.map((lang: any) => ({
        language: typeof lang === 'string' ? lang : (lang?.language || lang?.name || ''),
        name: typeof lang === 'string' ? lang : (lang?.name || lang?.language || ''),
        level: typeof lang === 'object' ? (lang?.level || lang?.proficiency || '') : '',
        proficiency: typeof lang === 'object' ? (lang?.proficiency || lang?.level || '') : ''
      })).filter((lang: any) => lang.language || lang.name);
      
      const normalizedOriginalLanguages = (cvData?.relevantLanguages || []).map((lang: any) => ({
        language: typeof lang === 'string' ? lang : (lang?.language || lang?.name || ''),
        name: typeof lang === 'string' ? lang : (lang?.name || lang?.language || ''),
        level: typeof lang === 'object' ? (lang?.level || lang?.proficiency || '') : '',
        proficiency: typeof lang === 'object' ? (lang?.proficiency || lang?.level || '') : ''
      })).filter((lang: any) => lang.language || lang.name);
      
      if (JSON.stringify(normalizedCurrentLanguages) !== JSON.stringify(normalizedOriginalLanguages)) {
        updateData.relevantLanguages = normalizedCurrentLanguages;
      }
      // Normalisiere Zertifikate für den Vergleich
      const normalizedCurrentCertifications = relevantCertifications.map((cert: any) => ({
        name: typeof cert === 'string' ? cert : (cert?.name || cert?.certificate || ''),
        certificate: typeof cert === 'string' ? cert : (cert?.certificate || cert?.name || ''),
        description: typeof cert === 'object' ? (cert?.description || '') : '',
        issuer: typeof cert === 'object' ? (cert?.issuer || '') : '',
        date: typeof cert === 'object' ? (cert?.date || '') : ''
      })).filter((cert: any) => cert.name || cert.certificate);
      
      const normalizedOriginalCertifications = (cvData?.relevantCertifications || []).map((cert: any) => ({
        name: typeof cert === 'string' ? cert : (cert?.name || cert?.certificate || ''),
        certificate: typeof cert === 'string' ? cert : (cert?.certificate || cert?.name || ''),
        description: typeof cert === 'object' ? (cert?.description || '') : '',
        issuer: typeof cert === 'object' ? (cert?.issuer || '') : '',
        date: typeof cert === 'object' ? (cert?.date || '') : ''
      })).filter((cert: any) => cert.name || cert.certificate);
      
      if (JSON.stringify(normalizedCurrentCertifications) !== JSON.stringify(normalizedOriginalCertifications)) {
        updateData.relevantCertifications = normalizedCurrentCertifications;
      }
      // Normalisiere Kernkompetenzen für den Vergleich
      const normalizedCurrentCoreCompetencies = relevantCoreCompetencies.map((comp: any) => ({
        competency: typeof comp === 'string' ? comp : (comp?.competency || comp?.name || '')
      })).filter((comp: any) => comp.competency);
      
      const normalizedOriginalCoreCompetencies = (cvData?.relevantCoreCompetencies || []).map((comp: any) => ({
        competency: typeof comp === 'string' ? comp : (comp?.competency || comp?.name || '')
      })).filter((comp: any) => comp.competency);
      
      if (JSON.stringify(normalizedCurrentCoreCompetencies) !== JSON.stringify(normalizedOriginalCoreCompetencies)) {
        updateData.relevantCoreCompetencies = normalizedCurrentCoreCompetencies;
      }
      // Normalisiere zusätzliche Informationen für den Vergleich
      const normalizedCurrentAdditionalInfo = relevantAdditionalInfo.map((info: any) => ({
        info: typeof info === 'string' ? info : (info?.info || ''),
        details: typeof info === 'object' ? (info?.details || '') : ''
      })).filter((info: any) => info.info);
      
      const originalAdditionalInfo = cvData?.relevantAdditionalInfo;
      let normalizedOriginalAdditionalInfo: Array<{ info: string; details: string }> = [];
      
      if (originalAdditionalInfo) {
        if (Array.isArray(originalAdditionalInfo)) {
          normalizedOriginalAdditionalInfo = originalAdditionalInfo.map((info: any) => ({
            info: typeof info === 'string' ? info : (info?.info || ''),
            details: typeof info === 'object' ? (info?.details || '') : ''
          })).filter((info: any) => info.info);
        } else if (typeof originalAdditionalInfo === 'string' && originalAdditionalInfo.trim()) {
          const lines = originalAdditionalInfo.split('\n').filter((line: string) => line.trim());
          normalizedOriginalAdditionalInfo = lines.map((line: string) => {
            if (line.includes(':')) {
              const [info, ...detailsParts] = line.split(':');
              return { info: info.trim(), details: detailsParts.join(':').trim() };
            }
            return { info: line.trim(), details: '' };
          });
        }
      }
      
      if (JSON.stringify(normalizedCurrentAdditionalInfo) !== JSON.stringify(normalizedOriginalAdditionalInfo)) {
        updateData.relevantAdditionalInfo = normalizedCurrentAdditionalInfo;
      }
      // Normalisiere Hobbys für den Vergleich
      const normalizedCurrentHobbies = relevantHobbies.map((hobby: any) => ({
        hobby: typeof hobby === 'string' ? hobby : (hobby?.hobby || hobby?.name || ''),
        name: typeof hobby === 'string' ? hobby : (hobby?.name || hobby?.hobby || ''),
        description: typeof hobby === 'object' ? (hobby?.description || '') : ''
      })).filter((hobby: any) => hobby.hobby || hobby.name);
      
      const normalizedOriginalHobbies = (cvData?.relevantHobbies || []).map((hobby: any) => ({
        hobby: typeof hobby === 'string' ? hobby : (hobby?.hobby || hobby?.name || ''),
        name: typeof hobby === 'string' ? hobby : (hobby?.name || hobby?.hobby || ''),
        description: typeof hobby === 'object' ? (hobby?.description || '') : ''
      })).filter((hobby: any) => hobby.hobby || hobby.name);
      
      if (JSON.stringify(normalizedCurrentHobbies) !== JSON.stringify(normalizedOriginalHobbies)) {
        updateData.relevantHobbies = normalizedCurrentHobbies;
      }
      // Vergleich für Berufserfahrungen - normalisiere die Daten zuerst
      const normalizedCurrent = (optimizedWorkExperiences || []).map(exp => ({
        position: exp.position || exp.title || '',
        company: exp.company || exp.employer || '',
        industry: exp.industry || '',
        startDate: exp.startDate || '',
        endDate: exp.endDate || '',
        description: exp.description || ''
      }));
      const normalizedOriginal = (cvData?.optimizedWorkExperiences || []).map(exp => ({
        position: exp.position || exp.title || '',
        company: exp.company || exp.employer || '',
        industry: exp.industry || '',
        startDate: exp.startDate || '',
        endDate: exp.endDate || '',
        description: exp.description || ''
      }));
      
      if (JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedOriginal)) {
        console.log('📝 Berufserfahrungen haben sich geändert:', {
          current: normalizedCurrent,
          original: normalizedOriginal
        });
        updateData.optimizedWorkExperiences = optimizedWorkExperiences;
      }
      // Normalisiere Ausbildungen für den Vergleich
      const normalizedCurrentEducations = relevantEducations.map((edu: any) => ({
        degree: typeof edu === 'string' ? edu : (edu?.degree || edu?.title || ''),
        title: typeof edu === 'string' ? edu : (edu?.title || edu?.degree || ''),
        institution: typeof edu === 'object' ? (edu?.institution || edu?.school || '') : '',
        school: typeof edu === 'object' ? (edu?.school || edu?.institution || '') : '',
        description: typeof edu === 'object' ? (edu?.description || '') : '',
        startDate: typeof edu === 'object' ? (edu?.startDate || '') : '',
        endDate: typeof edu === 'object' ? (edu?.endDate || '') : ''
      })).filter((edu: any) => edu.degree || edu.title);
      
      const normalizedOriginalEducations = (cvData?.relevantEducations || []).map((edu: any) => ({
        degree: typeof edu === 'string' ? edu : (edu?.degree || edu?.title || ''),
        title: typeof edu === 'string' ? edu : (edu?.title || edu?.degree || ''),
        institution: typeof edu === 'object' ? (edu?.institution || edu?.school || '') : '',
        school: typeof edu === 'object' ? (edu?.school || edu?.institution || '') : '',
        description: typeof edu === 'object' ? (edu?.description || '') : '',
        startDate: typeof edu === 'object' ? (edu?.startDate || '') : '',
        endDate: typeof edu === 'object' ? (edu?.endDate || '') : ''
      })).filter((edu: any) => edu.degree || edu.title);
      
      if (JSON.stringify(normalizedCurrentEducations) !== JSON.stringify(normalizedOriginalEducations)) {
        updateData.relevantEducations = normalizedCurrentEducations;
      }
      // Normalisiere Branchen für den Vergleich
      const normalizedCurrentIndustries = relevantIndustries.map((industry: any) => ({
        industry: typeof industry === 'string' ? industry : (industry?.industry || industry?.name || ''),
        name: typeof industry === 'string' ? industry : (industry?.name || industry?.industry || ''),
        years: typeof industry === 'object' ? (industry?.years || '') : ''
      })).filter((industry: any) => industry.industry || industry.name);
      
      const normalizedOriginalIndustries = (cvData?.relevantIndustries || []).map((industry: any) => ({
        industry: typeof industry === 'string' ? industry : (industry?.industry || industry?.name || ''),
        name: typeof industry === 'string' ? industry : (industry?.name || industry?.industry || ''),
        years: typeof industry === 'object' ? (industry?.years || '') : ''
      })).filter((industry: any) => industry.industry || industry.name);
      
      if (JSON.stringify(normalizedCurrentIndustries) !== JSON.stringify(normalizedOriginalIndustries)) {
        updateData.relevantIndustries = normalizedCurrentIndustries;
      }
      
      // Persönliche Informationen prüfen
      if (fullName !== (cvData?.fullName || '')) {
        updateData.fullName = fullName;
      }
      if (birthYear !== (cvData?.birthYear || '')) {
        updateData.birthYear = birthYear;
      }
      if (nationality !== (cvData?.nationality || '')) {
        updateData.nationality = nationality;
      }
      if (residencePermit !== (cvData?.residencePermit || '')) {
        updateData.residencePermit = residencePermit;
      }
      if (maritalStatus !== (cvData?.maritalStatus || '')) {
        updateData.maritalStatus = maritalStatus;
      }
      if (profileImage !== (cvData?.profileImage || '')) {
        updateData.profileImage = profileImage;
      }
      
      // Wenn es Änderungen gibt, speichere diese zuerst
      if (Object.keys(updateData).length > 0) {
        console.log('💾 Sende Update-Daten:', updateData);
        console.log('💾 Update-Daten (optimizedWorkExperiences):', updateData.optimizedWorkExperiences);
        
        const response = await fetch(`${config.backend.baseUrl}/api/cv-share/${token}/update`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Fehler beim Speichern');
        }
        
        setSuccess('CV-Daten erfolgreich gespeichert');
        // Warte kurz, damit das Backend die Daten gespeichert hat
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Setze Status auf "Freigegeben" und gebe CV-Daten frei (auch ohne Änderungen)
      try {
        // Rufe approve-Funktion auf (setzt automatisch Status auf "Freigegeben")
        const approveResponse = await fetch(`${config.backend.baseUrl}/api/cv-share/${token}/approve`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (approveResponse.ok) {
          console.log('✅ CV-Daten freigegeben und Status auf "Freigegeben" gesetzt');
          if (Object.keys(updateData).length === 0) {
            setSuccess('CV-Daten erfolgreich freigegeben');
          }
        } else {
          const errorData = await approveResponse.json();
          throw new Error(errorData.error || 'Fehler beim Freigeben');
        }
      } catch (approveError: any) {
        console.error('⚠️ Fehler beim Freigeben der CV-Daten:', approveError);
        throw new Error(approveError.message || 'Fehler beim Freigeben der CV-Daten');
      }
      
      // Markiere als gespeichert
      setIsSaved(true);
      
      // Lade Daten neu, um aktualisierte Werte zu erhalten
      console.log('🔄 Lade Daten nach dem Speichern neu...');
      await loadCvData();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setError('Fehler beim Speichern: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setSaving(false);
    }
  };
  
  const handleApprove = async () => {
    try {
      setApproving(true);
      setError(null);
      
      const response = await fetch(`${config.backend.baseUrl}/api/cv-share/${token}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Freigeben');
      }
      
      setSuccess('CV-Daten wurden erfolgreich freigegeben');
    } catch (err: any) {
      console.error('Fehler beim Freigeben:', err);
      setError('Fehler beim Freigeben: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setApproving(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Gemeinsamer Hintergrund-Style mit + Muster
  const backgroundStyle = {
    minHeight: '100vh',
    backgroundColor: '#e8e8e8',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d0d0d0' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
  };

  if (loading) {
    return (
      <Box sx={backgroundStyle}>
        <Container maxWidth="md" sx={{ pt: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            CV-Daten werden geladen...
          </Typography>
        </Container>
      </Box>
    );
  }
  
  if (error && isExpired) {
    return (
      <Box sx={backgroundStyle}>
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Alert severity="error">
            <Typography variant="h6">Link abgelaufen</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Dieser Link ist abgelaufen und kann nicht mehr verwendet werden.
            </Typography>
          </Alert>
        </Container>
      </Box>
    );
  }
  
  if (error && !cvData) {
    return (
      <Box sx={backgroundStyle}>
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Alert severity="error">
            <Typography variant="h6">Fehler</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          </Alert>
        </Container>
      </Box>
    );
  }
  
  return (
    <Box sx={backgroundStyle}>
      <Container maxWidth="md" sx={{ pt: 4, pb: 4, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Paper elevation={3} sx={{ p: 3, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 64px)', overflow: 'hidden' }}>
          {/* Fixer Header-Bereich */}
          <Box sx={{ flexShrink: 0 }}>
            <Box sx={{ mb: 3 }}>
              {/* Header mit Titel und Button auf gleicher Höhe */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h4">
                  CV-Informationen bearbeiten
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || isExpired || isSaved}
                  startIcon={saving ? <CircularProgress size={20} /> : isSaved ? <CheckCircleIcon /> : <SaveIcon />}
                  sx={{
                    background: isSaved
                      ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white !important',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '9999px',
                    px: 2.5,
                    py: 1,
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: isSaved
                        ? 'linear-gradient(135deg, #15803d 0%, #166534 100%)'
                        : 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      opacity: 0.7,
                      color: 'white !important',
                      transform: 'none',
                    },
                  }}
                >
                  {saving ? t('cv.share.saving') : isSaved ? t('cv.share.saved') : t('cv.share.save.and.approve')}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Bitte überprüfen und passen Sie die CV-Informationen an.
                </Typography>
                {expiresAt && !isSaved && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Gültig bis: {formatDate(expiresAt)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
            
            {/* Tabs Navigation */}
            <Tabs 
              value={tabIndex} 
              onChange={handleTabChange} 
              variant="scrollable" 
              scrollButtons="auto" 
              sx={{ 
                backgroundColor: 'white', 
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              <Tab label="Persönliche Informationen" />
              <Tab label="Berufserfahrung" />
              <Tab label="Ausbildung" />
              <Tab label="Weitere Angaben" />
            </Tabs>
          </Box>
          
          {/* Scrollbarer Content-Bereich */}
          <Box ref={contentRef} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pt: 2 }}>
          <fieldset
            disabled={isReadOnly}
            style={{
              margin: 0,
              padding: 0,
              border: 'none',
              minWidth: 0
            }}
          >
          
          {/* Tab 0: Persönliche Informationen */}
          {tabIndex === 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                {/* Linke Seite: Persönliche Angaben */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>Persönliche Angaben</Typography>
                  <TextField
                    fullWidth
                    label="Vollständiger Name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Jahrgang"
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    placeholder="z.B. 1985"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Nationalität"
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                    placeholder="z.B. CH, DE, AT"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Aufenthaltsbewilligung"
                    value={residencePermit}
                    onChange={(e) => setResidencePermit(e.target.value)}
                    placeholder="z.B. C, B, Schweizer Bürger"
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('cv.personal.civilstatus')}</InputLabel>
                    <Select
                      value={maritalStatus}
                      label={t('cv.personal.civilstatus')}
                      onChange={(e) => setMaritalStatus(e.target.value)}
                    >
                      <MenuItem value="">{t('cv.personal.civilstatus.select')}</MenuItem>
                      <MenuItem value="ledig">{t('cv.personal.civilstatus.single')}</MenuItem>
                      <MenuItem value="verheiratet">{t('cv.personal.civilstatus.married')}</MenuItem>
                      <MenuItem value="geschieden">{t('cv.personal.civilstatus.divorced')}</MenuItem>
                      <MenuItem value="verwitwet">{t('cv.personal.civilstatus.widowed')}</MenuItem>
                      <MenuItem value="eingetragene-partnerschaft">{t('cv.personal.civilstatus.registered-partnership')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                {/* Rechte Seite: Profilbild */}
                <Box sx={{ 
                  width: '220px', 
                  display: 'flex', 
                  flexDirection: 'column'
                }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Profilbild:
                  </Typography>
                  {profileImage ? (
                    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1/1' }}>
                      <img 
                        src={profileImage} 
                        alt="Profilbild" 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          border: '2px solid #e0e0e0'
                        }} 
                      />
                      <IconButton
                        size="small"
                        onClick={() => setProfileImage('')}
                        sx={{
                          position: 'absolute',
                          top: 5,
                          right: 5,
                          backgroundColor: 'rgba(255,255,255,0.8)',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                        }}
                        aria-label="Foto entfernen"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ 
                        aspectRatio: '1/1',
                        border: '2px dashed #1976d2', 
                        borderRadius: 2,
                        color: '#1976d2',
                        backgroundColor: '#f0f7ff',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                          borderColor: '#1565c0'
                        }
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '0.8rem' }}>
                        <span>Foto hochladen</span>
                        <span>(Kamera/Galerie)</span>
                      </Box>
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setProfileImage(reader.result as string);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </Button>
                  )}
                </Box>
              </Box>
            </>
          )}
          
          {/* Tab 1: Berufserfahrung */}
          {tabIndex === 1 && (
            <>
              {/* Kurzprofil */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Kurzprofil</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={optimizedShortProfile}
                  onChange={(e) => setOptimizedShortProfile(e.target.value)}
                  variant="outlined"
                  placeholder="Kurzprofil..."
                  helperText={!optimizedShortProfile ? "Kein Kurzprofil vorhanden" : ""}
                />
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              {/* Kernkompetenzen */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Kernkompetenzen</Typography>
                {relevantCoreCompetencies.length > 0 ? (
                  relevantCoreCompetencies.map((comp, index) => {
                    const competencyValue = typeof comp === 'string' ? comp : comp.competency || comp.name || '';
                    const isEditing = editingCoreCompetencyIndex === index;
                    return (
                      <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={competencyValue}
                            onChange={(e) => handleEditCoreCompetency(index, e.target.value)}
                            onBlur={() => setEditingCoreCompetencyIndex(null)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setEditingCoreCompetencyIndex(null); }}
                            autoFocus
                          />
                        ) : (
                          <>
                            <Typography variant="body1" sx={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditingCoreCompetencyIndex(index)}>
                              {competencyValue || 'Unbekannt'}
                            </Typography>
                            <IconButton size="small" color="error" onClick={() => handleDeleteCoreCompetency(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Paper>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Keine Kernkompetenzen vorhanden.</Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <TextField fullWidth size="small" placeholder="Neue Kernkompetenz hinzufügen..." value={newCoreCompetency} onChange={(e) => setNewCoreCompetency(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAddCoreCompetency(); }} />
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddCoreCompetency} disabled={!newCoreCompetency.trim()}>Hinzufügen</Button>
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              {/* Branchen */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Branchen</Typography>
                {relevantIndustries.length > 0 ? (
                  <>
                    {relevantIndustries.map((industry, index) => {
                      const industryName = industry.industry || industry.name || '';
                      const industryYears = industry.years || '';
                      const isEditing = editingIndustryIndex === index;
                      
                      return (
                        <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                          {isEditing ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Branche"
                                value={industryName}
                                onChange={(e) => handleEditIndustry(index, 'industry', e.target.value)}
                                autoFocus
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Jahre Erfahrung (optional)"
                                value={industryYears}
                                onChange={(e) => handleEditIndustry(index, 'years', e.target.value)}
                                placeholder="z.B. 5"
                              />
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  onClick={() => setEditingIndustryIndex(null)}
                                >
                                  Fertig
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography 
                                  variant="body1" 
                                  fontWeight="medium"
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() => setEditingIndustryIndex(index)}
                                >
                                  {industryName || 'Unbekannt'}
                                </Typography>
                                {industryYears && (
                                  <Typography variant="body2" color="text.secondary">
                                    {industryYears} Jahre Erfahrung
                                  </Typography>
                                )}
                              </Box>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteIndustry(index)}
                                aria-label="Branche löschen"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Keine Branchen vorhanden.
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Branche"
                      placeholder="z.B. IT, Finanzwesen..."
                      value={newIndustryName}
                      onChange={(e) => setNewIndustryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newIndustryName.trim()) {
                          handleAddIndustry();
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Jahre Erfahrung (optional)"
                      placeholder="z.B. 5"
                      value={newIndustryYears}
                      onChange={(e) => setNewIndustryYears(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newIndustryName.trim()) {
                          handleAddIndustry();
                        }
                      }}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddIndustry}
                    disabled={!newIndustryName.trim()}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Hinzufügen
                  </Button>
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              {/* Berufserfahrungen */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Berufserfahrungen</Typography>
                {optimizedWorkExperiences.filter(exp => exp.relevant === true || exp.relevant === 'true').length > 0 ? (
                  optimizedWorkExperiences.filter(exp => exp.relevant === true || exp.relevant === 'true').map((exp, filteredIndex) => {
                    const originalIndex = optimizedWorkExperiences.indexOf(exp);
                    return (
                    <Paper key={originalIndex} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <TextField fullWidth label="Position" value={exp.position || exp.title || ''} onChange={(e) => { const updated = [...optimizedWorkExperiences]; updated[originalIndex] = { ...updated[originalIndex], position: e.target.value, title: e.target.value }; setOptimizedWorkExperiences(updated); }} variant="outlined" sx={{ mb: 2 }} />
                      <TextField fullWidth label="Unternehmen" value={exp.company || exp.employer || ''} onChange={(e) => { const updated = [...optimizedWorkExperiences]; updated[originalIndex] = { ...updated[originalIndex], company: e.target.value, employer: e.target.value }; setOptimizedWorkExperiences(updated); }} variant="outlined" sx={{ mb: 2 }} />
                      <TextField fullWidth label="Branche" value={exp.industry || ''} onChange={(e) => { const updated = [...optimizedWorkExperiences]; updated[originalIndex] = { ...updated[originalIndex], industry: e.target.value }; setOptimizedWorkExperiences(updated); }} variant="outlined" sx={{ mb: 2 }} placeholder="z.B. IT, Finanzwesen, Gesundheitswesen..." />
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField fullWidth label="Von" value={exp.startDate || ''} onChange={(e) => { const updated = [...optimizedWorkExperiences]; updated[originalIndex] = { ...updated[originalIndex], startDate: e.target.value }; setOptimizedWorkExperiences(updated); }} variant="outlined" placeholder="z.B. 01.2020" />
                        <TextField fullWidth label="Bis" value={exp.endDate || ''} onChange={(e) => { const updated = [...optimizedWorkExperiences]; updated[originalIndex] = { ...updated[originalIndex], endDate: e.target.value }; setOptimizedWorkExperiences(updated); }} variant="outlined" placeholder="z.B. 12.2023 oder 'heute'" />
                      </Box>
                      <TextField fullWidth multiline rows={4} label="Beschreibung" value={exp.description || ''} onChange={(e) => { const updated = [...optimizedWorkExperiences]; updated[originalIndex] = { ...updated[originalIndex], description: e.target.value }; setOptimizedWorkExperiences(updated); }} variant="outlined" />
                    </Paper>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">Keine Berufserfahrungen vorhanden.</Typography>
                )}
              </Box>
            </>
          )}
          
          {/* Tab 2: Ausbildung */}
          {tabIndex === 2 && (
            <>
              {/* Ausbildungen */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Ausbildungen</Typography>
                {relevantEducations.length > 0 ? (
            <>
              {relevantEducations.map((edu, index) => {
                const isEditing = editingEducationIndex === index;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Abschluss"
                          value={edu.degree || edu.title || ''}
                          onChange={(e) => handleEditEducation(index, 'degree', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Institution"
                          value={edu.institution || edu.school || ''}
                          onChange={(e) => handleEditEducation(index, 'institution', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Beschreibung (optional)"
                          value={edu.description || ''}
                          onChange={(e) => handleEditEducation(index, 'description', e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Von (optional)"
                            value={edu.startDate || ''}
                            onChange={(e) => handleEditEducation(index, 'startDate', e.target.value)}
                            placeholder="z.B. 2015"
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Bis (optional)"
                            value={edu.endDate || ''}
                            onChange={(e) => handleEditEducation(index, 'endDate', e.target.value)}
                            placeholder="z.B. 2019"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingEducationIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => setEditingEducationIndex(index)}>
                            {edu.degree || edu.title || 'Abschluss'}
                          </Typography>
                          {edu.description && (
                            <Typography variant="body2" color="text.secondary">
                              {edu.description}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {edu.institution || edu.school || 'Institution'}
                          </Typography>
                          {(edu.startDate || edu.endDate) && (
                            <Typography variant="body2" color="text.secondary">
                              {edu.startDate || ''} {edu.endDate ? `- ${edu.endDate}` : ''}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteEducation(index)}
                          aria-label="Ausbildung löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Ausbildungen vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Abschluss"
              placeholder="z.B. Bachelor of Science"
              value={newEducationDegree}
              onChange={(e) => setNewEducationDegree(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Institution (optional)"
              placeholder="z.B. Universität XYZ"
              value={newEducationInstitution}
              onChange={(e) => setNewEducationInstitution(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Von (optional)"
                placeholder="z.B. 2015"
                value={newEducationStartDate}
                onChange={(e) => setNewEducationStartDate(e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Bis (optional)"
                placeholder="z.B. 2019"
                value={newEducationEndDate}
                onChange={(e) => setNewEducationEndDate(e.target.value)}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddEducation}
              disabled={!newEducationDegree.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Zertifikate */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Zertifikate
          </Typography>
          {relevantCertifications.length > 0 ? (
            <>
              {relevantCertifications.map((cert, index) => {
                const isEditing = editingCertificationIndex === index;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Zertifikat"
                          value={cert.name || cert.certificate || ''}
                          onChange={(e) => handleEditCertification(index, 'name', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Beschreibung (optional)"
                          value={cert.description || ''}
                          onChange={(e) => handleEditCertification(index, 'description', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Aussteller (optional)"
                          value={cert.issuer || ''}
                          onChange={(e) => handleEditCertification(index, 'issuer', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Datum (optional)"
                          value={cert.date || ''}
                          onChange={(e) => handleEditCertification(index, 'date', e.target.value)}
                          placeholder="z.B. 2023"
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingCertificationIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => setEditingCertificationIndex(index)}>
                            {cert.name || cert.certificate || 'Zertifikat'}
                          </Typography>
                          {cert.description && (
                            <Typography variant="body2" color="text.secondary">
                              {cert.description}
                            </Typography>
                          )}
                          {cert.issuer && (
                            <Typography variant="body2" color="text.secondary">
                              {cert.issuer}
                            </Typography>
                          )}
                          {cert.date && (
                            <Typography variant="body2" color="text.secondary">
                              {cert.date}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteCertification(index)}
                          aria-label="Zertifikat löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Zertifikate vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Zertifikat"
              placeholder="z.B. AWS Certified Solutions Architect"
              value={newCertificationName}
              onChange={(e) => setNewCertificationName(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Aussteller (optional)"
              placeholder="z.B. Amazon Web Services"
              value={newCertificationIssuer}
              onChange={(e) => setNewCertificationIssuer(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Datum (optional)"
              placeholder="z.B. 2023"
              value={newCertificationDate}
              onChange={(e) => setNewCertificationDate(e.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCertification}
              disabled={!newCertificationName.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
              </Button>
            </Box>
          </Box>
              
              <Divider sx={{ my: 3 }} />
              
              {/* Sprachen */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Sprachen</Typography>
                {relevantLanguages.length > 0 ? (
                  relevantLanguages.map((lang, index) => {
                    const langName = typeof lang === 'string' ? lang : lang.language || lang.name || '';
                    const langLevel = typeof lang === 'object' ? (lang.level || lang.proficiency || '') : '';
                    return (
                      <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField fullWidth size="small" value={langName} onChange={(e) => handleEditLanguage(index, 'language', e.target.value)} placeholder="Sprache" />
                          <FormControl fullWidth size="small">
                            <Select value={langLevel} onChange={(e) => handleEditLanguage(index, 'level', e.target.value)} displayEmpty>
                              <MenuItem value="">{t('cv.languages.level.select')}</MenuItem>
                              <MenuItem value="A1">{t('cv.languages.level.A1')}</MenuItem>
                              <MenuItem value="A2">{t('cv.languages.level.A2')}</MenuItem>
                              <MenuItem value="B1">{t('cv.languages.level.B1')}</MenuItem>
                              <MenuItem value="B2">{t('cv.languages.level.B2')}</MenuItem>
                              <MenuItem value="C1">{t('cv.languages.level.C1')}</MenuItem>
                              <MenuItem value="C2">{t('cv.languages.level.C2')}</MenuItem>
                              <MenuItem value="native">{t('cv.languages.level.native')}</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton size="small" color="error" onClick={() => handleDeleteLanguage(index)} aria-label="Sprache löschen"><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                      </Paper>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Keine Sprachen vorhanden.</Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField fullWidth size="small" label="Sprache" placeholder="z.B. Deutsch, Englisch..." value={newLanguageName} onChange={(e) => setNewLanguageName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && newLanguageName.trim()) handleAddLanguage(); }} />
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('cv.languages.level')}</InputLabel>
                      <Select value={newLanguageLevel} label={t('cv.languages.level')} onChange={(e) => setNewLanguageLevel(e.target.value)}>
                        <MenuItem value="">{t('cv.languages.level.select')}</MenuItem>
                        <MenuItem value="A1">{t('cv.languages.level.A1')}</MenuItem>
                        <MenuItem value="A2">{t('cv.languages.level.A2')}</MenuItem>
                        <MenuItem value="B1">{t('cv.languages.level.B1')}</MenuItem>
                        <MenuItem value="B2">{t('cv.languages.level.B2')}</MenuItem>
                        <MenuItem value="C1">{t('cv.languages.level.C1')}</MenuItem>
                        <MenuItem value="C2">{t('cv.languages.level.C2')}</MenuItem>
                        <MenuItem value="native">{t('cv.languages.level.native')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddLanguage} disabled={!newLanguageName.trim()} sx={{ alignSelf: 'flex-start' }}>Hinzufügen</Button>
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              {/* IT-Skills */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>IT-Kenntnisse</Typography>
                {relevantItSkills.length > 0 ? (
                  relevantItSkills.map((skill, index) => {
                    const skillName = typeof skill === 'string' ? skill : skill.name || skill.skill || '';
                    const skillLevel = typeof skill === 'object' ? (skill.level || skill.proficiency || '') : '';
                    return (
                      <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField fullWidth size="small" value={skillName} onChange={(e) => handleEditItSkill(index, 'skill', e.target.value)} placeholder="Skill-Name" />
                          <FormControl fullWidth size="small">
                            <Select value={skillLevel} onChange={(e) => handleEditItSkill(index, 'level', e.target.value)} displayEmpty>
                              <MenuItem value="">{t('cv.it.skills.level.select')}</MenuItem>
                              <MenuItem value="beginner">{t('cv.it.skills.level.beginner')}</MenuItem>
                              <MenuItem value="intermediate">{t('cv.it.skills.level.intermediate')}</MenuItem>
                              <MenuItem value="professional">{t('cv.it.skills.level.professional')}</MenuItem>
                              <MenuItem value="expert">{t('cv.it.skills.level.expert')}</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton size="small" color="error" onClick={() => handleDeleteItSkill(index)}><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                      </Paper>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Keine IT-Kenntnisse vorhanden.</Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField fullWidth size="small" label="Skill-Name" placeholder="z.B. JavaScript, Python..." value={newItSkillName} onChange={(e) => setNewItSkillName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && newItSkillName.trim()) handleAddItSkill(); }} />
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('cv.it.skills.level')}</InputLabel>
                      <Select value={newItSkillLevel} label={t('cv.it.skills.level')} onChange={(e) => setNewItSkillLevel(e.target.value)}>
                        <MenuItem value="">{t('cv.it.skills.level.select')}</MenuItem>
                        <MenuItem value="beginner">{t('cv.it.skills.level.beginner')}</MenuItem>
                        <MenuItem value="intermediate">{t('cv.it.skills.level.intermediate')}</MenuItem>
                        <MenuItem value="professional">{t('cv.it.skills.level.professional')}</MenuItem>
                        <MenuItem value="expert">{t('cv.it.skills.level.expert')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddItSkill} disabled={!newItSkillName.trim()} sx={{ alignSelf: 'flex-start' }}>Hinzufügen</Button>
                </Box>
              </Box>
            </>
          )}
          
          {/* Tab 3: Weitere Angaben */}
          {tabIndex === 3 && (
            <>
              {/* Hobbys */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Hobbys</Typography>
          {relevantHobbies.length > 0 ? (
            <>
              {relevantHobbies.map((hobby, index) => {
                const isEditing = editingHobbyIndex === index;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hobby"
                          value={hobby.hobby || hobby.name || ''}
                          onChange={(e) => handleEditHobby(index, 'hobby', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                          label="Beschreibung (optional)"
                          value={hobby.description || ''}
                          onChange={(e) => handleEditHobby(index, 'description', e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingHobbyIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => setEditingHobbyIndex(index)}>
                            {hobby.hobby || hobby.name || 'Hobby'}
                          </Typography>
                          {hobby.description && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {hobby.description}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteHobby(index)}
                          aria-label="Hobby löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine Hobbys vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Hobby"
              placeholder="z.B. Fotografie, Lesen..."
              value={newHobbyName}
              onChange={(e) => setNewHobbyName(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Beschreibung (optional)"
              placeholder="z.B. Leidenschaft für Landschaftsfotografie"
              value={newHobbyDescription}
              onChange={(e) => setNewHobbyDescription(e.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddHobby}
              disabled={!newHobbyName.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
            </Button>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        
        {/* Weitere Angaben */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Weitere Angaben
          </Typography>
          {relevantAdditionalInfo.length > 0 ? (
            <>
              {relevantAdditionalInfo.map((info, index) => {
                const isEditing = editingAdditionalInfoIndex === index;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Information"
                          value={info.info || ''}
                          onChange={(e) => handleEditAdditionalInfo(index, 'info', e.target.value)}
                          autoFocus
                        />
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                          label="Details (optional)"
                          value={info.details || ''}
                          onChange={(e) => handleEditAdditionalInfo(index, 'details', e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingAdditionalInfoIndex(null)}
                          >
                            Fertig
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => setEditingAdditionalInfoIndex(index)}>
                            {info.info || 'Information'}
                          </Typography>
                          {info.details && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {info.details}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAdditionalInfo(index)}
                          aria-label="Information löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine zusätzlichen Informationen vorhanden.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Information"
              placeholder="z.B. Führerschein, Ehrenamt..."
              value={newAdditionalInfoInfo}
              onChange={(e) => setNewAdditionalInfoInfo(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Details (optional)"
              placeholder="z.B. Klasse B, seit 2010"
              value={newAdditionalInfoDetails}
              onChange={(e) => setNewAdditionalInfoDetails(e.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddAdditionalInfo}
              disabled={!newAdditionalInfoInfo.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Hinzufügen
              </Button>
            </Box>
          </Box>
            </>
          )}
          </fieldset>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CvSharePage;

